/**
 * InlineBuddyGroupCreate -- Inline form to create a new buddy group during registration.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Loader2 } from 'lucide-react';
import { getDefaultBuddyGroupName } from '@/components/buddy-groups/getDefaultBuddyGroupName';

interface InlineBuddyGroupCreateProps {
  rodeoId: string;
  onCreated: (groupId: string) => void;
  createMutation: { mutateAsync: (data: any) => Promise<any>; isPending: boolean };
  /** Card owner org ID from the registration form's card identity field */
  cardOwnerOrgId?: string;
  /** Card owner user ID from the registration form's card identity field */
  cardOwnerUserId?: string;
  /** Existing buddy group names (any scope) used to compute the default name. */
  existingNames?: readonly (string | null | undefined)[];
}

export function InlineBuddyGroupCreate({ rodeoId, onCreated, createMutation, cardOwnerOrgId, cardOwnerUserId, existingNames }: InlineBuddyGroupCreateProps) {
  const [groupName, setGroupName] = useState(() => getDefaultBuddyGroupName(existingNames));
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!groupName.trim()) {
      setError('Please enter a group name');
      return;
    }
    setError(null);
    try {
      const payload: Record<string, string> = {
        rodeo_id: rodeoId,
        group_name: groupName.trim(),
      };
      if (cardOwnerOrgId) payload.primary_card_owner_org_id = cardOwnerOrgId;
      if (cardOwnerUserId) payload.primary_card_owner_user_id = cardOwnerUserId;
      const result = await createMutation.mutateAsync(payload);
      const groupId = (result?.buddy_group?.id ?? result?.data?.buddy_group?.id);
      if (groupId) {
        onCreated(groupId);
        setGroupName('');
      }
    } catch (err: any) {
      setError((err?.message ?? 'Failed to create group'));
    }
  };

  return (
    <div className="space-y-3 p-3 rounded-lg border border-dashed border-border">
      <Label className="text-sm font-medium">Create New Buddy Group</Label>
      <div className="flex gap-2">
        <Input
          placeholder="Group name..."
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          className="flex-1"
        />
        <Button
          type="button"
          size="sm"
          onClick={handleCreate}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
        </Button>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}
